import { Shipment } from '@pm/graphql';
import * as Sentry from '@sentry/react';
import { compareAsc, isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  getStatusMessage,
  getTranslatedStatusKey,
  shouldShowSupportLink,
} from '../../utilities/shipment';

type ShipmentTypeI18nKey = 'LabKit';

type ShipmentStatusResult = {
  status: string;
  description: string;
  trackingNumber: string;
  shouldShowSupportLink: boolean;
};

// Only the fields this hook actually reads — lets callers pass fragment shapes
// without casting to the full generated Shipment schema type.
export type ShipmentLike = Pick<
  Shipment,
  'updatedAt' | 'status' | 'trackingNumber'
>;

export const useShipmentStatus = ({
  inboundShipment = null,
  outboundShipment = null,
}: {
  inboundShipment?: ShipmentLike | null;
  outboundShipment?: ShipmentLike | null;
}): ShipmentStatusResult => {
  const { t } = useTranslation('shipment');

  const OUTBOUND_TYPE = 'outbound' as const;
  const INBOUND_TYPE = 'inbound' as const;

  const hasNoShipments = () => !inboundShipment && !outboundShipment;

  const hasOnlyOutboundShipment = () => outboundShipment && !inboundShipment;

  const hasOnlyInboundShipment = () => inboundShipment && !outboundShipment;

  const isOutboundMoreRecentThanInbound = () => {
    if (!outboundShipment || !inboundShipment) {
      return false;
    }

    const outboundLastUpdatedAt = parseISO(outboundShipment.updatedAt);
    const inboundLastUpdatedAt = parseISO(inboundShipment.updatedAt);

    if (!isValid(outboundLastUpdatedAt) || !isValid(inboundLastUpdatedAt)) {
      Sentry.captureException(
        new Error('Invalid date for shipment inbound or outbound'),
      );
      return false;
    }

    return compareAsc(outboundLastUpdatedAt, inboundLastUpdatedAt) > 0;
  };

  const getMostRelevantShipment = () => {
    if (hasNoShipments()) {
      return null;
    }

    if (hasOnlyOutboundShipment()) {
      return { shipment: outboundShipment, type: OUTBOUND_TYPE };
    }

    if (hasOnlyInboundShipment()) {
      return { shipment: inboundShipment, type: INBOUND_TYPE };
    }

    if (isOutboundMoreRecentThanInbound()) {
      return { shipment: outboundShipment, type: OUTBOUND_TYPE };
    }

    return { shipment: inboundShipment, type: INBOUND_TYPE };
  };

  const primaryShipment = getMostRelevantShipment();

  if (!primaryShipment) {
    return {
      status: t('LabKit.NoStatus'),
      description: t('LabKit.ShippingStatus.USPSTrackingSiteWithSupport'),
      trackingNumber: t('LabKit.NoTrackingNumber'),
      shouldShowSupportLink: true,
    };
  }

  const { shipment, type } = primaryShipment;
  const status = shipment?.status ?? '';
  const shipmentTranslationKey: ShipmentTypeI18nKey = 'LabKit';
  const descriptionKeyTemplate = getStatusMessage({
    status,
    type,
    shipmentTranslationKey,
  });
  const statusKeyTemplate = getTranslatedStatusKey({
    statusKey: status,
    shipmentTranslationKey,
  });
  const trackingNumber: string = shipment?.trackingNumber ?? '';

  const result = {
    status: t(statusKeyTemplate),
    description: t(descriptionKeyTemplate),
    trackingNumber,
    shouldShowSupportLink: shouldShowSupportLink(status),
  };

  return result;
};
